import { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FiCheckCircle, FiXCircle, FiEye } from "react-icons/fi";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

export default function ApplicationList() {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [openLoading, setOpenLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [editStatus, setEditStatus] = useState("");
  const [editRemarks, setEditRemarks] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);

  useEffect(() => {
    if (selectedApplication) {
      // Application selected
    }
  }, [selectedApplication]);

  const fetchApplications = useCallback(async () => {
    try {
      setOpenLoading(true);
      const response = await axiosPrivate.get("/applications");
      setApplications(response.data);
      setOpenLoading(false);
    } catch (error) {
      console.error("Error fetching applications:", error);
      alert("Error: " + error.message);
      setOpenLoading(false);
    }
  }, [axiosPrivate]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    if (statusFilter === "all") {
      setFilteredApplications(applications);
    } else {
      setFilteredApplications(
        applications.filter((app) => app.status === statusFilter),
      );
    }
  }, [statusFilter, applications]);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleViewClick = (params) => {
    setSelectedApplication(params.row);
    setEditStatus(params.row.status);
    setEditRemarks(params.row.remarks || "");
    setIsEditing(false);
    setOpenDetail(true);
  };

  const handleSaveStatus = async () => {
    try {
      setOpenLoading(true);
      await axiosPrivate.patch(
        `/applications/${selectedApplication.id}/status`,
        {
          status: editStatus,
          remarks: editRemarks,
        },
      );

      await fetchApplications();
      setIsEditing(false);
      setOpenDetail(false);
      setOpenLoading(false);
    } catch (error) {
      console.error("Error updating application:", error);
      alert("Failed to update status: " + error.message);
      setOpenLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setOpenLoading(true);
      await axiosPrivate.delete(`/applications/${selectedApplication.id}`);
      await fetchApplications();
      setOpenDeleteConfirm(false);
      setOpenDetail(false);
      setOpenLoading(false);
    } catch (error) {
      console.error("Error deleting application:", error);
      alert("Failed to delete application: " + error.message);
      setOpenLoading(false);
    }
  };

  const handleAddAsClient = () => {
    // Helper function to format dates to MySQL format (YYYY-MM-DD)
    const formatDate = (dateString) => {
      if (!dateString) return null;
      const date = new Date(dateString);
      return date.toISOString().split("T")[0]; // Returns YYYY-MM-DD
    };

    // Map application data to client form structure
    const clientData = {
      CIFKEY: selectedApplication.employeeNo || "", // Use employee number as CIFKEY
      ClientName: `${selectedApplication.givenName} ${selectedApplication.surname}`,
      Gender: selectedApplication.gender,
      DateOfBirth: formatDate(selectedApplication.birthdate),
      Occupation: selectedApplication.occupation,
      Department: selectedApplication.department,
      Section: selectedApplication.section,
      Add1: selectedApplication.street,
      Add2: selectedApplication.suburb,
      Lot: selectedApplication.lot,
      HomePhone: selectedApplication.homePhone,
      BusPhone: selectedApplication.businessPhone,
      BusFax: selectedApplication.businessFax,
      Email: selectedApplication.email,
      MaritalStatus: selectedApplication.maritalStatus,
      NextLeaveDate: formatDate(selectedApplication.nextDateOfLeave),
      AccNo: selectedApplication.bankAccountNo,
      BankName: selectedApplication.bankAccountName,
      TraType: selectedApplication.bankAccountType,
      Remarks: `Created from application ID: ${selectedApplication.id}`,
    };

    // Navigate to AddClient with pre-filled data and flag to indicate new client
    navigate("/addClient", {
      state: { ...clientData, isNewFromApplication: true },
    });
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: "warning", label: "Pending" },
      approved: { color: "success", label: "Approved" },
      declined: { color: "error", label: "Declined" },
      processing: { color: "info", label: "Processing" },
    };

    const config = statusConfig[status] || { color: "default", label: status };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      renderCell: (params) => (
        <Button
          size="small"
          startIcon={<FiEye />}
          onClick={() => handleViewClick(params)}
        >
          View
        </Button>
      ),
    },
    {
      field: "givenName",
      headerName: "Given Name",
      flex: 1.5,
    },
    {
      field: "surname",
      headerName: "Surname",
      flex: 1.5,
    },
    {
      field: "department",
      headerName: "Department",
      flex: 2,
    },
    {
      field: "loanAmount",
      headerName: "Loan Amount",
      flex: 1.5,
      valueFormatter: (params) => `$${Number(params.value).toLocaleString()}`,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.5,
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: "createdAt",
      headerName: "Applied Date",
      flex: 2,
      valueFormatter: (params) =>
        dayjs(params.value).format("YYYY-MM-DD HH:mm"),
    },
  ];

  return (
    <Box sx={{ height: "100vh", width: "100%", p: 2 }}>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <Typography variant="h5">Loan Applications</Typography>
        <Typography variant="body2" color="text.secondary">
          ({filteredApplications.length} applications)
        </Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            label="Status Filter"
            onChange={handleStatusFilterChange}
          >
            <MenuItem value="all">All Applications</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="declined">Declined</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={fetchApplications}>
          Refresh
        </Button>
      </Stack>

      <DataGrid
        rows={filteredApplications}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        checkboxSelection
        disableRowSelectionOnClick
        slots={{ toolbar: GridToolbar }}
        sx={{ bgcolor: "background.paper" }}
      />

      {/* Detail Dialog */}
      <Dialog
        open={openDetail && selectedApplication !== null}
        onClose={() => {
          setOpenDetail(false);
          setSelectedApplication(null);
        }}
        maxWidth="md"
        fullWidth
        maxHeight="90vh"
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Application Details</Typography>
            {selectedApplication && getStatusChip(selectedApplication.status)}
          </Stack>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            maxHeight: "calc(90vh - 120px)",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#a1a1a1",
            },
          }}
        >
          {selectedApplication ? (
            <Box sx={{ p: 1 }}>
              {/* Status Management - Moved to top for better accessibility */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: "#e3f2fd",
                  borderRadius: 1,
                  border: "1px solid #1976d2",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  gutterBottom
                  color="primary"
                >
                  Status Management
                </Typography>
                {isEditing ? (
                  <Stack spacing={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={editStatus}
                        label="Status"
                        onChange={(e) => setEditStatus(e.target.value)}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="declined">Declined</MenuItem>
                        <MenuItem value="processing">Processing</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Remarks"
                      value={editRemarks}
                      onChange={(e) => setEditRemarks(e.target.value)}
                      size="small"
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveStatus}
                      >
                        Save Changes
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setEditStatus(selectedApplication.status);
                          setEditRemarks(selectedApplication.remarks || "");
                          setIsEditing(false);
                        }}
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ minWidth: "80px" }}
                      >
                        Current Status:
                      </Typography>
                      {getStatusChip(selectedApplication.status)}
                    </Stack>
                    {selectedApplication.remarks && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Remarks:
                        </Typography>
                        <Typography variant="body2">
                          {selectedApplication.remarks}
                        </Typography>
                      </Box>
                    )}
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Applied:{" "}
                        {dayjs(selectedApplication.createdAt).format(
                          "YYYY-MM-DD HH:mm",
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Updated:{" "}
                        {dayjs(selectedApplication.updatedAt).format(
                          "YYYY-MM-DD HH:mm",
                        )}
                      </Typography>
                    </Stack>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => setIsEditing(true)}
                      sx={{ alignSelf: "flex-start", mt: 1 }}
                    >
                      Edit Status
                    </Button>
                  </Stack>
                )}
              </Box>
              {/* Applicant Information */}
              <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    Applicant Information
                  </Typography>
                  {/* <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={handleAddAsClient}
                  >
                    Add as a new client
                  </Button> */}
                </Stack>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Full Name
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.givenName}{" "}
                      {selectedApplication.surname}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Employee No
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.employeeNo || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Gender
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.gender || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Date of Birth
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.birthdate
                        ? dayjs(selectedApplication.birthdate).format(
                            "YYYY-MM-DD",
                          )
                        : "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Marital Status
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.maritalStatus || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Email
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {selectedApplication.email || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Mobile Phone
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.mobilePhone || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Home Phone
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.homePhone || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Business Phone
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.businessPhone || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Business Fax
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.businessFax || "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Address Information */}
              {(selectedApplication.street ||
                selectedApplication.suburb ||
                selectedApplication.lot) && (
                <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    Address Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Street
                      </Typography>
                      <Typography variant="body1">
                        {selectedApplication.street || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Suburb
                      </Typography>
                      <Typography variant="body1">
                        {selectedApplication.suburb || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Lot
                      </Typography>
                      <Typography variant="body1">
                        {selectedApplication.lot || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Employment Details */}
              <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  Employment Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Department
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.department || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Section
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.section || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Occupation
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.occupation || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Years of Service
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.yearsOfService
                        ? `${selectedApplication.yearsOfService} years`
                        : "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Next Leave Date
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.nextDateOfLeave
                        ? dayjs(selectedApplication.nextDateOfLeave).format(
                            "YYYY-MM-DD",
                          )
                        : "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Bank Account Information */}
              {(selectedApplication.bankAccountName ||
                selectedApplication.bankAccountNo ||
                selectedApplication.bankAccountType) && (
                <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    Bank Account Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Bank Name
                      </Typography>
                      <Typography variant="body1">
                        {selectedApplication.bankAccountName || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Account Number
                      </Typography>
                      <Typography variant="body1">
                        {selectedApplication.bankAccountNo || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Account Type
                      </Typography>
                      <Typography variant="body1">
                        {selectedApplication.bankAccountType || "N/A"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Loan Details */}
              <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  Loan Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Loan Purpose
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.loanPurpose || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Loan Amount
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="primary"
                    >
                      $
                      {Number(
                        selectedApplication.loanAmount || 0,
                      ).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Number of FNs
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.numberOfFNs || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Repayment per FN
                    </Typography>
                    <Typography variant="body1">
                      $
                      {Number(
                        selectedApplication.repaymentPerFN || 0,
                      ).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          ) : (
            <Typography>No application selected</Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            bgcolor: "#fafafa",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button
            variant="outlined"
            color="error"
            onClick={() => setOpenDeleteConfirm(true)}
            sx={{ mr: 1 }}
          >
            Delete Application
          </Button>
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={() => setOpenDetail(false)}
              sx={{ minWidth: "100px" }}
            >
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleAddAsClient}
              color="success"
              sx={{ minWidth: "140px" }}
            >
              Add as Client
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this application? This action cannot
            be undone.
          </Typography>
          {selectedApplication && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Applicant: {selectedApplication.givenName}{" "}
              {selectedApplication.surname}
              <br />
              ID: {selectedApplication.id}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Dialog */}
      <Dialog open={openLoading}>
        <DialogContent>
          <Stack alignItems="center" spacing={2} sx={{ p: 2 }}>
            <CircularProgress />
            <Typography>Loading...</Typography>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
